import React from 'react';
import { 
    FloppyFill as FloppyIcon,
    TrashFill as TrashIcon,
    ThreeDotsResponsive as AdditionalOptionsIcon,
    PlusCircleFill as Plus,
    PencilFill
} from './Icons';
import { addClassNames } from '../../Functions/Helper/Component';
import { bindOnChangeArray } from '../../Functions/Helper/OnChange';
import { v4 as uuidv4 } from 'uuid';
import './ArrayInput.scss';
import Orderable from './Orderable';
import useActionDialog from '../../Provider/ActionDialogContext';
import { useDraggingContext } from './Draggable';

function DefaultCollapseButton(label) {

    return ({setCollapsed}) => (
        <button type="button" className="btn btn-primary mb-4 align-self-end" onClick={() => setCollapsed(false)}>
            <Plus className="me-2"/> {label || "Add"}</button>
    );
}


function getItemForm({
    InputComponent,
    addButtonProps,
    updateButtonProps,
    saveButtonProps,
    submitButtonProps,
    cancelButtonProps,
    isNew,
    onItemSubmit,
    onItemRemove,
    noActionComponents,
    className
}) {
    const SubmitButton = function ({ value, disabled, type, onSubmit }) { 

        const {label, children, onClick, ...props} = Object.assign(
            {}, 
            (submitButtonProps || {}), 
            ((
                isNew && noActionComponents ? saveButtonProps 
                : isNew ? addButtonProps 
                : updateButtonProps
            ) || {})
        );

        const submit = (e) => {
            if (typeof onClick !== "function" || onClick(e) !== false) {
                onSubmit();
            }
        }
        return (
            <button
                type={type || "button"}
                className={"btn btn-primary submit-button " +  (isNew? "edit-item-submit" : "new-item-submit")}
                onClick={(e) => {
                    e.value = value;
                    if (typeof onClick === "function") onClick(e);
                    if (type !== "submit") submit(e);
                }}
                disabled={disabled}
                {...props}
            >{children || (<>
                <FloppyIcon className="me-2"/>{label || (isNew? "Add" : "Update")}
            </>)}</button> 
        );
    };

    const CancelButton = function ({ value, onCancel }) {
        const {label, children, onClick, ...props} = cancelButtonProps || {};
        return (
            <button
                type="button"
                className="btn btn-outline-secondary mx-4"
                onClick={(e) => {
                    e.value = value;
                    if (typeof onClick !== "function" || onClick(e) !== false) {
                        onCancel()
                    }
                }}
                {...props}
            >{children || (label || "Cancel")}</button>
        );
    }
    
    const ActionComponent = function ({onSubmit, onCancel, submitType, ...props}) {
            return (
                <div className="action-components d-flex justify-content-end">
                    {!isNew && <CancelButton onCancel={onCancel} {...props} />}
                    <SubmitButton type={submitType || "button"} onSubmit={onSubmit} {...props} />
                </div>
            );
        
        };

    return function ({id, onCancel, Collapsable, CollapseNew, arr, ...props}) {
        const formContainerRef = React.useRef();
        const [value, setValue] = React.useState(props.value || {});
        const onSubmit = React.useCallback(() => {
            let result;
            if (isNew) {
                result = onItemSubmit(value, props.value);
                setValue({});
                CollapseNew();
            } else {
                result = onItemSubmit(props.index, value, props.value);
                onCancel();
            }
            return result;
        }, [props.index, props.value, CollapseNew, value, onCancel]);

        return (
            <div id={id} ref={formContainerRef} className={addClassNames("array-input-item d-flex flex-column gap-1", className)}>
                <InputComponent
                    value={value}
                    onChange={(_value) => {
                        setValue(_value);
                    }}
                    isNew={isNew}
                    Collapsable={Collapsable}
                    Collapse={CollapseNew}
                    onSubmit={onSubmit}
                    formContainerRef={formContainerRef}
                    onRemove={() => {
                        onItemRemove(props.index, props.value);
                    }}
                    onCancel={onCancel}
                    ActionComponent={ActionComponent}
                    arr={arr}
                />
                {noActionComponents? <></> :  (
                    <ActionComponent 
                        onSubmit={onSubmit}
                        onCancel={onCancel}
                        value={value}
                    />
                )}
            </div>
            
        )
    }

}

function getItemDisplay({
    DisplayComponent,
    additionalActions,
    removeButtonProps,
    editButtonProps,
    displayWrapperClassName,
    onItemSubmit,
    onItemRemove,
    noClickWrapper,
    className
}) {

    const AdditionalOptions = ({ value, index, onEdit }) => {
        const [dropdown, setDropdown] = React.useState(false);
        const {
            label: removeLabel,
            children: removeChildren,
            onClick: removeOnClick,
            ...removeProps
        } = removeButtonProps || {};
        const {
            label: editLabel,
            children: editChildren,
            onClick: editOnClick,
            ...editProps
        } = editButtonProps || {};
        const { confirm } = useActionDialog();
        return (
            <div className={addClassNames("dropdown-container right", className)}>
                <button
                    type="button"
                    className="btn btn-outline-secondary p-2"
                    onClick={() => setDropdown(!dropdown)}
                    onBlur={(e) => {
                        setDropdown(false);
                        const relatedTarget = e.relatedTarget;
                        if (
                            relatedTarget 
                            && e.target.parentNode.contains(relatedTarget)
                            && relatedTarget.tagName === "BUTTON"
                            && typeof relatedTarget.click === "function"
                        ) {
                            relatedTarget.click();
                        }
                    }}
                ><AdditionalOptionsIcon/></button>
                <ul className={addClassNames("dropdown-menu",(dropdown?"show" : ""))}>
                    {additionalActions && additionalActions.map(([label, action], i) => (
                        <li key={i}>
                            <button 
                                type="button"
                                className="dropdown-item"
                                onClick={() => {action(index, value)}}
                            >{label}</button>
                        </li>
                    ))}
                    {additionalActions && <li><hr className="dropdown-divider"/></li>}
                    <li key="edit-button">
                        <button
                            type="button"
                            className="dropdown-item"
                            onClick={async (e) => {
                                if (typeof editOnClick !== "function" || editOnClick(e) !== false) {
                                    onEdit(true);
                                }
                            }}
                            {...editProps}
                        >{editChildren || (<>
                            <PencilFill className="me-2"/>{editLabel? editLabel : " Edit"}
                        </>)}</button>
                        
                    </li>
                    <li key="remove-button">
                        <button
                            type="button"
                            className="dropdown-item text-danger"
                            onClick={async (e) => {
                                e.index = index;
                                e.value = value;
                                if (await confirm("This could not be undone. Are you sure?"))
                                if (typeof removeOnClick !== "function" || removeOnClick(e) !== false) {
                                    onItemRemove(index, value);
                                }
                            }}
                            {...removeProps}
                        >{removeChildren || (<>
                            <TrashIcon className="me-2"/>{removeLabel? removeLabel : " Delete"}
                        </>)}</button>

                    </li>
                </ul>
            </div>
        );
    }

    const DisplayComponentWrapper = ({ index, value, onEdit }) => {
        return (
            <div {...(noClickWrapper?{}:{ onClick: (e) => {
                e.preventDefault();
                onEdit();
            }})} className={addClassNames("display-item-container flex-grow-1 me-1", displayWrapperClassName)}>
                <DisplayComponent onChange={(_value) => {
                    onItemSubmit(index, _value, value)
                }} value={value} onEdit={onEdit} />
            </div>
        );
    }
    return function ({ onEdit, ...props}) {
        return (
            <>
                <DisplayComponentWrapper onEdit={onEdit} {...props}/>
                <AdditionalOptions onEdit={onEdit} {...props}/>
            </>
            
        );
    }
}
function getItemComponent({
    itemClassName, inputClassName, 
    editItemClassName, editItemLabel,
    ...getProps}) {

    const EditRenderer = getItemForm({
        ...getProps,
        className: inputClassName || itemClassName || "",
        isNew: false
    });

    const DisplayRenderer = getProps.DisplayComponent && getItemDisplay({
        className: itemClassName || "", ...getProps
    });
    
    return function ({initiallyEdit, ...props}) {
        const [editing, _setEditing] = React.useState(initiallyEdit);
        const dragging = useDraggingContext();
        const setDraggable = dragging?.setDraggable;
        const setEditing = React.useCallback((val) => {
            _setEditing(val);
            if (setDraggable) setDraggable(!val);
        }, [setDraggable]);
        if (editing || !DisplayRenderer) {
            return (
                <div className={addClassNames('new-item', editItemClassName)}>
                    {editItemLabel && ( <h3 className="mb-3 fs-5">
                        {typeof editItemLabel === "function"? editItemLabel(props.value) : editItemLabel}
                    </h3> ) }
                    <EditRenderer
                        {...props}
                        onCancel={() => setEditing(false)}
                    />
                </div>
                
            ); 
        } else {
            return (
                <DisplayRenderer
                    {...props}
                    onEdit={() => setEditing(true)}
                />
                
            );
        }
    }
}

export default function ArrayInput({
    InputComponent,
    DisplayComponent,
    additionalActions,
    addButtonProps,
    updateButtonProps,
    saveButtonProps,
    submitButtonProps,
    cancelButtonProps,
    editButtonProps,
    removeButtonProps,
    noActionComponents,
    itemClassName,
    inputClassName,
    values,
    newValue,
    keyFunction,
    editItemLabel,
    editItemClassName,
    label,
    newItemLabel,
    newItemClassName,
    displayWrapperClassName,
    className,
    children,
    newButtonLabel,
    CollapsedNewView: _CollapseNewView,
    defaultNewCollapsed,
    noClickWrapper,
    onChange
}) {
    const [isCollapsed, setNewCollapsed] = React.useState(defaultNewCollapsed || false);
    const [newItemId] = React.useState(uuidv4());
    const CollapsedNewView = React.useMemo(() => {
        if (_CollapseNewView) return _CollapseNewView;
        else if (newButtonLabel) return DefaultCollapseButton(newButtonLabel);
    }, [_CollapseNewView, newButtonLabel]);
    const containerRef = React.useRef();
    const moveUp = React.useCallback((index) => {
        if (index <= 0) return;
        onChange([
            ...values.slice(0, index - 1),
            values[index],
            values[index - 1],
            ...values.slice(index + 1)
        ])
    }, [values, onChange]);

    const moveDown = React.useCallback((index) => {
        if (index + 1 >= values.length) return;

        onChange([
            ...values.slice(0, index),
            values[index + 1],
            values[index],
            ...values.slice(index + 2)
        ])
    }, [values, onChange]);

    const ItemComponent = React.useMemo(() => getItemComponent({
        InputComponent,
        DisplayComponent,
        updateButtonProps,
        saveButtonProps,
        submitButtonProps,
        cancelButtonProps,
        removeButtonProps,
        editButtonProps,
        additionalActions,
        editItemLabel,
        editItemClassName,
        inputClassName,
        itemClassName,
        displayWrapperClassName,
        noClickWrapper,
        onItemSubmit: (index, value) => {
            if (typeof onChange === "function") {
                return bindOnChangeArray(onChange, index)(value);
            }
        },
        onItemRemove: (index) => {
            if (typeof onChange === "function") {
                return onChange((values) => {
                    const newValues = values.slice();
                    newValues.splice(index, 1);
                    return newValues;
                });
            }
        },
        noActionComponents
    }), [
        InputComponent,
        DisplayComponent,
        updateButtonProps,
        saveButtonProps,
        submitButtonProps,
        cancelButtonProps,
        editButtonProps,
        removeButtonProps,
        additionalActions,
        editItemLabel,
        editItemClassName,
        onChange,
        inputClassName,
        itemClassName, 
        noActionComponents,
        noClickWrapper,
        displayWrapperClassName
    ]);

    const NewItemForm = React.useMemo(() => getItemForm({
        InputComponent,
        addButtonProps,
        saveButtonProps,
        submitButtonProps,
        cancelButtonProps,
        isNew: true,
        value: newValue,
        inputClassName,
        itemClassName,
        onItemSubmit: (value) => {
            if (typeof onChange === "function") return onChange((values) => {
                const newValues = (values || []).slice();
                const _value = {...value, key: uuidv4()};
                newValues.push(_value);
                return newValues;
            });
        },
        noActionComponents
    }), [
        InputComponent,
        addButtonProps,
        saveButtonProps,
        submitButtonProps,
        cancelButtonProps,
        newValue,
        onChange,
        inputClassName,
        itemClassName,
        noActionComponents
    ]);
    return (
        <div ref={containerRef} className={addClassNames("input array-input", className)}>
            <div className="d-flex flex-row flex-nowrap justify-content-between align-items-center my-3">
                {(children ? children : label? <label>{label}</label> : <></>)} 
                {values.length > 2 && CollapsedNewView && <CollapsedNewView setCollapsed={() => {
                    setNewCollapsed(false);
                    setTimeout(() => {
                        document.getElementById("new-item-" + newItemId)?.scrollIntoView({
                            behavior: "smooth"
                        });
                    }, 250);
                }}/>}
            </div>
            <div className="values d-flex flex-column gap-3">
                <Orderable
                    onChange={onChange}
                    values={values}
                    ItemComponent={ItemComponent}
                    keyFunction={keyFunction}
                    moveUp={moveUp}
                    moveDown={moveDown}
                />
                {CollapsedNewView && isCollapsed ?
                    (<CollapsedNewView setCollapsed={setNewCollapsed}/>)
                    : (
                        <div id={"new-item-" + newItemId} className={addClassNames('new-item', newItemClassName)}>
                            {newItemLabel && ( <h3 className="mb-3 fs-5">{newItemLabel}</h3> ) }
                            
                                <NewItemForm
                                    key="new-item"
                                    CollapseNew={() => setNewCollapsed(true)}
                                    Collapsable={!!CollapsedNewView}
                                    arr={values}
                                    className={addClassNames(`new-item`, inputClassName || itemClassName || "")}
                                />
                        </div>
                    )
                }
            </div>
        </div>
    );
}